import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import MobileProjectsPage from "./pages/MobileProjectsPage";
import MobileAgentCloudPage from "./pages/MobileAgentCloudPage";
import MobileChatPage from "./pages/MobileChatPage";
import MobileEnvPage from "./pages/MobileEnvPage";
import MobileGuard from "./components/MobileGuard";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";

// Pages
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import PortfolioPage from "@/pages/PortfolioPage";
import ContactPage from "@/pages/ContactPage";
import OnboardingPage from "@/pages/OnboardingPage";
import LoginPage from "@/pages/LoginPage";
import ExecutiveCockpitPage from "@/pages/ExecutiveCockpitPage";
import RoadmapPage from "@/pages/RoadmapPage";
import IncubatorPage from "@/pages/IncubatorPage";
import SessionsPage from "@/pages/SessionsPage";
import MemoirePage from "@/pages/MemoirePage";
import EmailsPage from "@/pages/EmailsPage";
import EmailingPage from "@/pages/EmailingPage";
import MessagingPage from "@/pages/MessagingPage";
import ValidationsPage from "@/pages/ValidationsPage";
import AgentsPermanentsPage from "@/pages/AgentsPermanentsPage";
import CommercialSupervisorPage from "@/pages/CommercialSupervisorPage";
import ProjectActivitePage from "@/pages/ProjectActivitePage";
import OrganizerPage from "@/pages/OrganizerPage";
import TasksPage from "@/pages/TasksPage";
import WorkspacePage from "@/pages/WorkspacePage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import OpportunityDetailPage from "@/pages/OpportunityDetailPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ClientsPage from "@/pages/ClientsPage";
import SettingsPage from "@/pages/SettingsPage";
import ProjectCDCPage from "@/pages/ProjectCDCPage";
import ProjectCommercialPage from "@/pages/ProjectCommercialPage";
import ProjectChatPage from "@/pages/ProjectChatPage";
import ProjectAgentsPage from "@/pages/ProjectAgentsPage";
import EcosystemPage from "@/pages/EcosystemPage";
import InfrastructurePage from "@/pages/InfrastructurePage";
import SitesPage from "@/pages/SitesPage";
import SecuritePage from "@/pages/SecuritePage";
import AgentPage from "@/pages/AgentPage";
import AtelierPage from "@/pages/AtelierPage";
import PubStudioPage from "@/pages/PubStudioPage";
import ProjectGitPage from "@/pages/ProjectGitPage";
import ProjectAccessPage from "@/pages/ProjectAccessPage";
import ProjectFilesPage from "@/pages/ProjectFilesPage";
import ProjectServerPage from "@/pages/ProjectServerPage";
import ProjectRoadmapPage from "./pages/ProjectRoadmapPage";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("odo_studio_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            logout();
          }
        } catch (error) {
          console.error("Token validation failed:", error);
          logout();
        }
      }
      setLoading(false);
    };
    validateToken();
  }, [token]);

  const login = (accessToken, userData) => {
    localStorage.setItem("odo_studio_token", accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("odo_studio_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (["collaborator", "observer"].includes(user.role) && !["/dashboard/tasks", "/dashboard/messaging"].includes(window.location.pathname)) {
    return <Navigate to="/dashboard/tasks" replace />;
  }

  if (user.role === "intern") {
    const path = window.location.pathname;
    const allowed = path === "/dashboard/tasks" || path === "/dashboard/messaging" || path.startsWith("/dashboard/projects")
      || path === "/m" || path.startsWith("/m/projet/");
    if (!allowed) return <Navigate to="/dashboard/projects" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="app-wrapper">
        <BrowserRouter>
          <MobileGuard />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/login" element={<LoginPage />} />

            <Route path="/m" element={<ProtectedRoute><MobileProjectsPage /></ProtectedRoute>} />
            <Route path="/m/agents" element={<ProtectedRoute><MobileAgentCloudPage /></ProtectedRoute>} />
            <Route path="/m/projet/:projectId" element={<ProtectedRoute><MobileChatPage /></ProtectedRoute>} />
            <Route path="/m/projet/:projectId/acces" element={<ProtectedRoute><MobileEnvPage /></ProtectedRoute>} />
            {/* Protected CRM Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <ExecutiveCockpitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/roadmap"
              element={
                <ProtectedRoute>
                  <RoadmapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/workspace"
              element={
                <ProtectedRoute>
                  <WorkspacePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/tasks"
              element={
                <ProtectedRoute>
                  <TasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/organizer"
              element={
                <ProtectedRoute>
                  <OrganizerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/sessions"
              element={
                <ProtectedRoute>
                  <SessionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/incubator"
              element={
                <ProtectedRoute>
                  <IncubatorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/clients"
              element={
                <ProtectedRoute>
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/opportunities"
              element={
                <ProtectedRoute>
                  <OpportunitiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/opportunities/:oppId"
              element={
                <ProtectedRoute>
                  <OpportunityDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/commercial"
              element={
                <ProtectedRoute>
                  <ProjectCommercialPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/cdc"
              element={
                <ProtectedRoute>
                  <ProjectCDCPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/roadmap"
              element={
                <ProtectedRoute>
                  <ProjectRoadmapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/ecosystem"
              element={
                <ProtectedRoute>
                  <EcosystemPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/securite"
              element={
                <ProtectedRoute>
                  <SecuritePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/agent"
              element={
                <ProtectedRoute>
                  <AgentPage />
                </ProtectedRoute>
              }
            />
            <Route path="/dashboard/superviseur-commercial" element={<ProtectedRoute><CommercialSupervisorPage /></ProtectedRoute>} />
            <Route
              path="/dashboard/atelier"
              element={
                <ProtectedRoute>
                  <AtelierPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/sites"
              element={
                <ProtectedRoute>
                  <SitesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/infrastructure"
              element={
                <ProtectedRoute>
                  <InfrastructurePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/server"
              element={
                <ProtectedRoute>
                  <ProjectServerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/files"
              element={
                <ProtectedRoute>
                  <ProjectFilesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/access"
              element={
                <ProtectedRoute>
                  <ProjectAccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/agents-permanents"
              element={
                <ProtectedRoute>
                  <AgentsPermanentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/validations"
              element={
                <ProtectedRoute>
                  <ValidationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/emails"
              element={
                <ProtectedRoute>
                  <EmailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/memoire"
              element={
                <ProtectedRoute>
                  <MemoirePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/activite"
              element={
                <ProtectedRoute>
                  <ProjectActivitePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/git"
              element={
                <ProtectedRoute>
                  <ProjectGitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/chat"
              element={
                <ProtectedRoute>
                  <ProjectChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId/agents"
              element={
                <ProtectedRoute>
                  <ProjectAgentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            <Route path="/dashboard/pub-studio" element={<ProtectedRoute><PubStudioPage /></ProtectedRoute>} />
            <Route path="/dashboard/emailing" element={<ProtectedRoute><EmailingPage /></ProtectedRoute>} />
            <Route path="/dashboard/messaging" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
